import React from 'react'
import { Link } from 'react-router-dom'
import googlePlay from '../assets/img/svg/google-play.svg'
import app from '../assets/img/svg/apple-store.svg'
import fbIcon from '../assets/img/social/facebook.png'
import igIcon from '../assets/img/social/instagram.png'
import liIcon from '../assets/img/social/business.png'
import twIcon from '../assets/img/social/twitter.png'
import ytIcon from '../assets/img/social/youtube.png'
import { useState, useEffect } from 'react'
import { API_BASE, getImageUrl } from '../utils/api'

export default function Footer() {
    const [whiteLogo, setWhiteLogo] = useState<string>('/white-logo.png')

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const res = await fetch(`${API_BASE}/system-settings`);
                const data = await res.json();
                if (data && data.whiteLogo) {
                    setWhiteLogo(getImageUrl(data.whiteLogo));
                }
            } catch (err) {
                console.error('Failed to fetch footer logo:', err);
            }
        };
        fetchLogo();
    }, [])
    return (
        <footer className="bg-[#020826] text-white pt-16 md:pt-24 pb-12 border-t border-white/5 relative z-0 overflow-hidden" style={{ background: 'linear-gradient(to bottom, #020826, #05144b, #4c1d95)' }}>
            {/* Premium Background Glows */}
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#f1ae24]/5 rounded-full blur-[140px] -translate-y-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#4c1d95] rounded-full blur-[120px] -mr-32 -mb-32 pointer-events-none opacity-40"></div>

            <div className="max-w-[1500px] mx-auto px-4 relative z-10">

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:gap-16 gap-8 mb-4 whitespace-normal">

                    {/* Branding */}
                    <div className="lg:col-span-3">
                        <Link className="inline-block mb-6 group" to="/">
                            <img src={whiteLogo} className="h-20 w-auto transition-transform duration-500 group-hover:scale-105" alt="My Property Guru" />
                        </Link>
                        <p className="text-slate-400 text-[15px] leading-relaxed mb-6 font-medium">
                            Your trusted partner in premium real estate across Gujarat. Connecting you with excellence since 2010.
                        </p>
                        <div className="flex gap-3">
                            {[
                                { icon: 'fa-facebook-f', link: 'https://www.facebook.com/profile.php?id=100078325324646' },
                                { icon: 'fa-instagram', link: 'https://www.instagram.com/mypropertyguru?igsh=aHc5NnhlN3MzdWFl&utm_source=qr' },
                                { icon: 'fa-youtube', link: 'https://www.youtube.com/@mypropertyguru' },
                                { icon: 'fa-linkedin-in', link: 'https://www.linkedin.com/in/my-property-guru/' },
                                { icon: 'fa-twitter', link: 'https://x.com/Mypropertyguru1' }
                            ].map((s, i) => (
                                <Link key={i} to={s.link} target="_blank" className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 bg-white/5 border border-white/20 hover:border-white/60 shadow-sm group overflow-hidden text-white">
                                    <i className={`fa-brands ${s.icon} text-white text-[14px] group-hover:scale-110 transition-transform`}></i>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Quick Navigation & Categories Wrapper */}
                    <div className="grid grid-cols-2 col-span-1 sm:contents lg:col-span-4 gap-4 items-start">
                        {/* Quick Navigation - Spanning 2 columns on desktop */}
                        <div className="w-full flex flex-col items-start text-left lg:col-span-2">
                            <h4 className="text-base font-bold uppercase tracking-wider mb-8 text-[#f1ae24] text-left">Quick Links</h4>
                            <ul className="space-y-4 p-0 m-0 list-none flex flex-col items-start">
                                {[
                                    { name: 'Home', path: '/' },
                                    { name: 'About Projects', path: '/project-list' },
                                    { name: 'Top Builders', path: '/developer-list' },
                                    { name: 'Expert Blog', path: '/blog' },
                                    { name: 'Contact Us', path: '/contact' }
                                ].map((item) => (
                                    <li key={item.name} className="w-full text-left">
                                        <Link to={item.path} className="text-slate-400 hover:text-[#f1ae24] text-[15px] font-medium transition-all duration-300 flex items-center justify-start group">
                                            <span className="h-[2px] bg-[#f1ae24] w-0 group-hover:w-2 transition-all duration-300 group-hover:mr-2"></span>
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
 
                        {/* Categories - Spanning 2 columns on desktop */}
                        <div className="w-full flex flex-col items-start text-left lg:col-span-2">
                            <h4 className="text-base font-bold uppercase tracking-wider mb-8 text-[#f1ae24] text-left">Categories</h4>
                            <ul className="space-y-4 p-0 m-0 list-none flex flex-col items-start">
                                {['Residential', 'Commercial', 'Plot & Land', 'Industrial', 'Bank Auction'].map((item) => (
                                    <li key={item} className="w-full text-left">
                                        <Link to={`/project-list?category=${encodeURIComponent(item)}`} className="text-slate-400 hover:text-[#f1ae24] text-[15px] font-medium transition-all duration-300 flex items-center justify-start group">
                                            <span className="h-[2px] bg-[#f1ae24] w-0 group-hover:w-2 transition-all duration-300 group-hover:mr-2"></span>
                                            {item}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Contact Info - Expanded to prevent email overflow */}
                    <div className="lg:col-span-3">
                        <h4 className="text-base font-bold uppercase tracking-wider mb-8 text-[#f1ae24]">Get In Touch</h4>
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center border border-white/10 shrink-0">
                                    <i className="fa-solid fa-phone text-[10px] text-[#f1ae24]"></i>
                                </div>
                                <a href="tel:+919537002666" className="text-[15px] font-medium text-slate-300 hover:text-[#f1ae24] transition-colors leading-tight">+91 95370 02666</a>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center border border-white/10 shrink-0">
                                    <i className="fa-solid fa-envelope text-[10px] text-[#f1ae24]"></i>
                                </div>
                                <a href="mailto:info@mypropertyguru.in" className="text-[15px] font-medium text-slate-300 hover:text-[#f1ae24] transition-colors leading-tight">info@mypropertyguru.in</a>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center border border-white/10 shrink-0">
                                    <i className="fa-solid fa-location-dot text-[10px] text-[#f1ae24]"></i>
                                </div>
                                <span className="text-[15px] font-medium leading-relaxed text-slate-400 font-medium">A-332, money plant high st, gota <br /> ahmedabad, gujarat 382470</span>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Apps */}
                    <div className="lg:col-span-2 flex flex-col items-center lg:items-end">
                        <h4 className="text-base font-bold uppercase tracking-wider mb-6 sm:mb-10 text-[#f1ae24] whitespace-nowrap">Experience Mobile</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-3 w-fit">
                            <Link to="#" className="group bg-white/5 border border-white/10 py-2.5 px-3 rounded-xl flex items-center justify-center sm:justify-start gap-2.5 hover:bg-white/10 hover:border-[#f1ae24]/50 transition-all duration-300 w-fit">
                                <img src={googlePlay} alt="Play Store" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform" />
                                <div className="leading-tight">
                                    <div className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-[#f1ae24] mb-0.5 whitespace-nowrap">PLAY STORE</div>
                                    <div className="text-[9px] sm:text-[11px] font-black uppercase tracking-wide text-white whitespace-nowrap">Download app</div>
                                </div>
                            </Link>
                            <Link to="#" className="group bg-white/5 border border-white/10 py-2.5 px-3 rounded-xl flex items-center justify-center sm:justify-start gap-2.5 hover:bg-white/10 hover:border-[#f1ae24]/50 transition-all duration-300 w-fit">
                                <img src={app} alt="App Store" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform" />
                                <div className="leading-tight">
                                    <div className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-[#f1ae24] mb-0.5 whitespace-nowrap">APP STORE</div>
                                    <div className="text-[9px] sm:text-[11px] font-black uppercase tracking-wide text-white whitespace-nowrap">Download app</div>
                                </div>
                            </Link>
                        </div>
                    </div>

                </div>

                {/* Sub-Footer */}
                <div className="pt-4 md:pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
                    <p className="text-sm font-medium text-slate-500 text-center md:text-left">
                        © {new Date().getFullYear()} <span className="text-slate-400">My Property Guru.</span> Precision in every pixel.
                    </p>
                    <div className="flex items-center gap-6 md:gap-12 flex-wrap justify-center md:justify-end">
                        <Link to="/privacy-policy" className="text-sm font-medium text-slate-500 hover:text-[#f1ae24] transition-colors">Privacy Policy</Link>
                        <Link to="/terms" className="text-sm font-medium text-slate-500 hover:text-[#f1ae24] transition-colors">Terms of Service</Link>
                        <Link to="#" className="text-sm font-medium text-slate-500 hover:text-[#f1ae24] transition-colors">Site Map</Link>
                    </div>
                </div>

            </div>
        </footer>
    );
}
